import React, { useState } from 'react';
import { 
  BookOpen,
  Target,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  FileText,
  Layers,
  Award,
  Calendar,
  Users,
  Lightbulb,
  ClipboardList
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Year 13 ICT - Scheme of Work Data
const schemeOfWorkData = {
  subject: "Year 13 ICT",
  year: 2025,
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Personal Computer Systems",
      subtitle: "ICT Trends & Information Systems",
      color: "blue",
      terms: ["Term 1"],
      totalWeeks: 12,
      totalMarks: 80,
      units: [
        {
          id: "1.1",
          title: "Future ICT Trends and Their Key Features",
          weeks: "1-3",
          hours: 6,
          outcomes: [
            "Identify key future trends in digital technologies and Internet",
            "State key features of identified future ICT trends in education",
            "State key features of identified future ICT trends in health",
            "State key features of identified future ICT trends in home"
          ],
          topics: [
            "AI-powered personalized learning platforms",
            "Telehealth and remote patient monitoring",
            "Smart home ecosystems powered by IoT",
            "Virtual Reality in education and training"
          ],
          assessments: [
            { type: "Quiz", title: "ICT Trends Quiz", marks: 15 }
          ],
          resources: ["Computer", "Internet", "Projector", "Research materials"]
        },
        {
          id: "1.2",
          title: "Workflows and Information Systems",
          weeks: "4-9",
          hours: 12,
          outcomes: [
            "Define workflow and identify its parts",
            "Describe workflow processes in organizations",
            "Explain the relationship between workflows and IS",
            "Identify IS applications and their characteristics",
            "Explain benefits of IS in education and government",
            "Understand IS development lifecycle"
          ],
          topics: [
            "Workflow components and processes",
            "Information Systems fundamentals",
            "Student Information Systems (SIS)",
            "Tourism Management Systems",
            "IS development and implementation",
            "Waterfall model for IS development"
          ],
          assessments: [
            { type: "Project", title: "IS Case Study Project", marks: 25 }
          ],
          resources: ["Computer", "Internet", "Case studies", "Local organization examples"]
        },
        {
          id: "1.3",
          title: "ICT Infrastructure and Networking",
          weeks: "10-12",
          hours: 6,
          outcomes: [
            "Understand ICT infrastructure components",
            "Identify implementation concerns for local organizations",
            "State and explain key features of network types",
            "Compare open-source vs proprietary software",
            "Install and manage software applications"
          ],
          topics: [
            "Hardware infrastructure components",
            "Network types: LAN, WAN, MAN, Wi-Fi",
            "Implementation concerns in Vanuatu context",
            "Open-source vs proprietary software",
            "Software installation and management"
          ],
          assessments: [
            { type: "Test", title: "Strand 1 Term Test", marks: 40 }
          ],
          resources: ["Computer", "Network diagrams", "Software installation media"]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Website Development",
      subtitle: "Digital Media & Web Design",
      color: "green",
      terms: ["Term 2"],
      totalWeeks: 11,
      totalMarks: 100,
      units: [
        {
          id: "2.1",
          title: "Digital Media Fundamentals",
          weeks: "1-6",
          hours: 14,
          outcomes: [
            "Understand importance of digital media",
            "Identify necessary audio/video/graphics devices",
            "Identify requirements for creating media content",
            "Understand basic software operations",
            "Modify audio, graphics, and video to meet requirements"
          ],
          topics: [
            "Digital media importance and types",
            "Audio/video/graphics equipment",
            "Content creation requirements",
            "Software tools for media editing",
            "Basic and advanced media modification",
            "Media integration techniques"
          ],
          assessments: [
            { type: "Practical", title: "Digital Media Creation", marks: 30 }
          ],
          resources: ["Computer", "Digital media software (Audacity, GIMP)", "Media files"]
        },
        {
          id: "2.2",
          title: "Website Development Fundamentals",
          weeks: "7-11",
          hours: 15,
          outcomes: [
            "Identify web design requirements",
            "Define CSS and state types of CSS",
            "Describe CSS syntax and declarations",
            "Explain advantages of CSS",
            "Link CSS with HTML",
            "Create database-driven websites"
          ],
          topics: [
            "Web design requirements and principles",
            "CSS types: inline, internal, external",
            "CSS syntax, selectors, and declarations",
            "HTML and CSS integration",
            "Database-driven website concepts",
            "Website testing and validation"
          ],
          assessments: [
            { type: "Project", title: "Website Development Project", marks: 40 },
            { type: "Test", title: "Strand 2 Term Test", marks: 30 }
          ],
          resources: ["Computer", "Text editor/IDE", "Web browsers", "Database tools"]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Programming",
      subtitle: "Problem Solving & Software Development",
      color: "purple",
      terms: ["Term 3"],
      totalWeeks: 9,
      totalMarks: 110,
      units: [
        {
          id: "3.1",
          title: "Problem Solving Fundamentals",
          weeks: "1-2",
          hours: 4,
          outcomes: [
            "Understand concepts of problem-solving",
            "Apply problem-solving steps to scenarios",
            "Analyze problems systematically",
            "Develop solution strategies"
          ],
          topics: [
            "Problem-solving concepts and importance",
            "Four steps: Understand, Plan, Implement, Test",
            "Problem analysis techniques",
            "Solution strategy development"
          ],
          assessments: [
            { type: "Quiz", title: "Problem Solving Quiz", marks: 15 }
          ],
          resources: ["Whiteboard", "Problem scenarios", "Worksheets"]
        },
        {
          id: "3.2",
          title: "Programming Concepts and Design",
          weeks: "3-9",
          hours: 18,
          outcomes: [
            "Use Top-Down and Bottom-Up design approaches",
            "Represent solutions using pseudocode",
            "Represent solutions using flowcharts",
            "Understand variables, data types, and operators",
            "Apply modular programming concepts",
            "Demonstrate coding competency"
          ],
          topics: [
            "Top-Down vs Bottom-Up design",
            "Pseudocode conventions and writing",
            "Flowchart symbols and creation",
            "Variables and data types",
            "Operators and expressions",
            "Functions and modular programming",
            "Coding practice and projects"
          ],
          assessments: [
            { type: "Practical", title: "Pseudocode & Flowcharts", marks: 25 },
            { type: "Project", title: "Programming Project", marks: 40 },
            { type: "Test", title: "Strand 3 Term Test", marks: 30 }
          ],
          resources: ["Computer", "Programming IDE", "Code examples", "Project templates"]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Microprocessor Programming",
      subtitle: "Embedded Systems & ICT Ethics",
      color: "red",
      terms: ["Term 4"],
      totalWeeks: 6,
      totalMarks: 170,
      units: [
        {
          id: "4.1",
          title: "Microprocessors and Embedded Systems",
          weeks: "1-4",
          hours: 10,
          outcomes: [
            "Understand microprocessor architecture and components",
            "Explain role of compilers, interpreters, and assemblers",
            "Understand sensors, actuators, and interfacing",
            "Explain importance of embedded systems",
            "Identify applications of programmable microprocessors"
          ],
          topics: [
            "Microprocessor components: ALU, Control Unit, Registers, Bus",
            "Code translation: compilers vs interpreters",
            "Sensors and actuators",
            "Analog-to-Digital conversion",
            "Embedded systems applications",
            "Real-world microprocessor applications"
          ],
          assessments: [
            { type: "Quiz", title: "Microprocessors Quiz", marks: 20 }
          ],
          resources: ["Microprocessor diagrams", "Embedded system examples", "Sensors/actuators"]
        },
        {
          id: "4.2",
          title: "ICT Ethics and Social Issues",
          weeks: "5-6",
          hours: 4,
          outcomes: [
            "Understand ethical concerns in ICT use",
            "Know legislations to minimize ethical issues",
            "Understand environmental concerns with ICT",
            "Understand e-waste management",
            "Understand health issues related to ICT"
          ],
          topics: [
            "Privacy and data protection",
            "Intellectual property and copyright",
            "Digital divide and accessibility",
            "Cyberbullying and online behavior",
            "Environmental impact of ICT",
            "E-waste management",
            "Health issues: ergonomics, screen time"
          ],
          assessments: [
            { type: "Project", title: "Ethics Case Study", marks: 20 },
            { type: "Exam", title: "Final Examination", marks: 130 }
          ],
          resources: ["Case studies", "Research materials", "Discussion prompts"]
        }
      ]
    }
  ]
};

// Get strand color classes
const getStrandColors = (color) => {
  const colors = {
    blue: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700', gradient: 'from-blue-500 to-blue-600' },
    green: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', gradient: 'from-green-500 to-green-600' },
    purple: { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-700', gradient: 'from-purple-500 to-purple-600' },
    red: { bg: 'bg-red-500', light: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', gradient: 'from-red-500 to-red-600' }
  };
  return colors[color] || colors.blue;
};

export default function EmbeddedSchemeOfWork() {
  const [selectedStrand, setSelectedStrand] = useState('strand1');
  const [expandedUnit, setExpandedUnit] = useState(null);
  
  const scheme = schemeOfWorkData;
  const currentStrand = scheme.strands.find(s => s.id === selectedStrand);
  const colors = getStrandColors(currentStrand?.color || 'blue');
  
  // Calculate totals
  const totalMarks = scheme.strands.reduce((acc, s) => acc + s.totalMarks, 0);
  const totalUnits = scheme.strands.reduce((acc, s) => acc + s.units.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className={`bg-gradient-to-r ${colors.gradient} text-white`}>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Layers className="h-6 w-6" />
                Scheme of Work - {scheme.subject}
              </CardTitle>
              <CardDescription className="text-white/80 mt-1">
                {scheme.strands.length} Strands • {totalUnits} Units • {totalMarks} Total Marks
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm">
                <FileText className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Strand Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {scheme.strands.map((strand) => {
          const strandColors = getStrandColors(strand.color);
          return (
            <Card 
              key={strand.id}
              className={`cursor-pointer transition-all hover:shadow-lg ${selectedStrand === strand.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedStrand(strand.id)}
            >
              <CardHeader className={`pb-2 bg-gradient-to-r ${strandColors.gradient} text-white rounded-t-lg`}>
                <Badge variant="secondary" className="w-fit mb-1">Strand {strand.number}</Badge>
                <CardTitle className="text-base">{strand.title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <p className="text-xs text-muted-foreground mb-2">{strand.subtitle}</p>
                <div className="flex justify-between text-sm">
                  <span>{strand.units.length} Units</span>
                  <span className="font-semibold">{strand.totalMarks} marks</span>
                </div>
                <div className="flex gap-1 mt-2">
                  {strand.terms.map((t, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Strand Tabs */}
      <Tabs value={selectedStrand} onValueChange={setSelectedStrand}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="strand1">Strand 1</TabsTrigger>
          <TabsTrigger value="strand2">Strand 2</TabsTrigger>
          <TabsTrigger value="strand3">Strand 3</TabsTrigger>
          <TabsTrigger value="strand4">Strand 4</TabsTrigger>
        </TabsList>

        {scheme.strands.map((strand) => {
          const strandColors = getStrandColors(strand.color);
          return (
            <TabsContent key={strand.id} value={strand.id} className="space-y-4 mt-4">
              {/* Strand Header */}
              <Card className={`border-l-4 ${strandColors.border} ${strandColors.light}`}>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={strandColors.bg}>Strand {strand.number}</Badge>
                        {strand.terms.map((t, i) => (
                          <Badge key={i} variant="outline">{t}</Badge>
                        ))}
                      </div>
                      <CardTitle className="text-xl">{strand.title}</CardTitle>
                      <CardDescription>{strand.subtitle}</CardDescription>
                    </div>
                    <div className="flex gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">{strand.units.length}</p>
                        <p className="text-xs text-muted-foreground">Units</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">{strand.totalWeeks}</p>
                        <p className="text-xs text-muted-foreground">Weeks</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">{strand.totalMarks}</p>
                        <p className="text-xs text-muted-foreground">Marks</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Units */}
              <Accordion type="single" collapsible className="space-y-2">
                {strand.units.map((unit) => (
                  <AccordionItem key={unit.id} value={unit.id} className="border rounded-lg">
                    <AccordionTrigger className="px-4 hover:no-underline">
                      <div className="flex items-center gap-4 text-left">
                        <Badge className={strandColors.bg}>Unit {unit.id}</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">{unit.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Weeks {unit.weeks} • {unit.hours} hours
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {unit.assessments.map((a, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {a.type}: {a.marks}m
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        {/* Learning Outcomes */}
                        <div>
                          <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                            <Target className={`h-4 w-4 ${strandColors.text}`} />
                            Learning Outcomes
                          </h4>
                          <ul className="space-y-2">
                            {unit.outcomes.map((outcome, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                {outcome}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Topics */}
                        <div>
                          <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                            <BookOpen className={`h-4 w-4 ${strandColors.text}`} />
                            Topics Covered
                          </h4>
                          <ul className="space-y-2">
                            {unit.topics.map((topic, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                {topic}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Assessments & Resources */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className={`p-4 rounded-lg ${strandColors.light}`}>
                          <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                            <Award className={`h-4 w-4 ${strandColors.text}`} />
                            Assessments
                          </h4>
                          <div className="space-y-2">
                            {unit.assessments.map((assessment, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-white rounded">
                                <div>
                                  <p className="font-medium text-sm">{assessment.title}</p>
                                  <Badge variant="outline" className="text-xs mt-1">{assessment.type}</Badge>
                                </div>
                                <p className="text-lg font-bold text-primary">{assessment.marks}m</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-gray-50">
                          <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                            <ClipboardList className="h-4 w-4 text-gray-600" />
                            Resources Required
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {unit.resources.map((resource, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {resource}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Strand Summary Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" />
                    Strand {strand.number} Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unit</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Weeks</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Outcomes</TableHead>
                        <TableHead className="text-right">Marks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {strand.units.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell>
                            <Badge variant="outline">{unit.id}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{unit.title}</TableCell>
                          <TableCell>{unit.weeks}</TableCell>
                          <TableCell>{unit.hours}</TableCell>
                          <TableCell>{unit.outcomes.length}</TableCell>
                          <TableCell className="text-right font-bold">
                            {unit.assessments.reduce((acc, a) => acc + a.marks, 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell colSpan={5}>Strand Total</TableCell>
                        <TableCell className="text-right text-primary">{strand.totalMarks}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
